/**
 * 자격증 스포츠 종목 / 단체 / 단체별 레벨 데이터
 *
 * - LICENSE_SPORTS: 스포츠 종목 선택 목록 (value = 내부값, label = 노출명)
 * - LICENSE_COMPANIES: 단체 선택 드롭다운 목록 (순서 = 노출 순서)
 * - LICENSE_LEVELS: 단체별 수강생(student)/강사(instructor) 레벨 목록.
 *   각 레벨은 { name, sport } 형태로, 어떤 스포츠 종목인지 sport 값으로 구분한다.
 *
 * 조회는 getLicenseCompanies() / getLicenseLevels()를 사용한다.
 * 자격증 등록 플로우: 스포츠 → 강사 여부 → 단체 → 레벨 → 사진 업로드
 * @see pages/mypage/VerifyLicense.jsx
 */

export const LICENSE_SPORTS = [
    { value: "scuba", label: "스쿠버" },
    { value: "freediving", label: "프리다이빙" },
    { value: "mermaid", label: "머메이드" },
];

export const LICENSE_COMPANIES = [
    "PADI",
    "AIDA",
    "SSI",
    "RAID",
    "PSA",
    "MOLCHANOVS",
    "SDI",
    "WASE",
];

/** 이름 목록을 { name, sport } 배열로 태깅한다. */
const tag = (sport, names) => names.map((name) => ({ name, sport }));

export const LICENSE_LEVELS = {
    PADI: {
        student: [
            ...tag("freediving", [
                "BASIC FREEDIVER",
                "FREEDIVER",
                "ADVANCED FREEDIVER",
                "MASTER FREEDIVER",
            ]),
            ...tag("mermaid", [
                "BASIC MERMAID",
                "MERMAID",
                "ADVANCED MERMAID",
                "MASTER MERMAID",
            ]),
            ...tag("scuba", [
                "OPEN WATER SCUBA DIVER",
                "ADVANCED OPEN WATER SCUBA DIVER",
                "RESCUE SCUBA DIVER",
            ]),
        ],
        instructor: [
            ...tag("freediving", [
                "FREEDIVER INSTRUCTOR",
                "MASTER FREEDIVER INSTRUCTOR",
                "ADVANCED FREEDIVER INSTRUCTOR",
                "FREEDIVER INSTRUCTOR TRAINER",
            ]),
            ...tag("mermaid", ["MERMAID INSTRUCTOR"]),
        ],
    },
    AIDA: {
        student: tag("freediving", [
            "1",
            "2",
            "3",
            "4",
            "AIDA MONOFIN FREEDIVER",
        ]),
        instructor: tag("freediving", ["FREEDIVING INSTRUCTOR"]),
    },
    SSI: {
        student: [
            ...tag("freediving", [
                "BASIC FREEDIVING",
                "LEVEL1 FREEDIVING",
                "LEVEL2 FREEDIVING",
                "LEVEL3 FREEDIVING",
            ]),
            ...tag("mermaid", [
                "TRY MERMAID",
                "MERMAID",
                "OCEAN MERMAID",
                "MODEL MERMAID",
            ]),
            ...tag("scuba", [
                "OPEN WATER DIVER",
                "ADVANCED OPEN WATER DIVER",
                "RESCUE DIVER",
                "MASTER DIVER",
            ]),
        ],
        instructor: [
            ...tag("freediving", [
                "BASIC FREEDIVING INSTRUCTOR",
                "FREEDIVING INSTRUCTOR",
                "ADVACED FREEDIVING INSTRUCTOR",
                "FREEDIVING INSTRUCTOR TRAINER",
            ]),
            ...tag("mermaid", ["MERMAID INSTRUCTOR"]),
        ],
    },
    RAID: {
        student: tag("freediving", [
            "BASIC FREEDIVER",
            "FREEDIVER",
            "ADVANCED FREEDIVER",
            "MASTER FREEDIVER",
        ]),
        instructor: tag("freediving", [
            "FREEDIVING INSTRUCTOR",
            "ADVACED INSTRUCTOR",
            "MASTER INSTRUCTOR",
        ]),
    },
    PSA: {
        student: tag("freediving", [
            "BASIC FREEDIVER",
            "OPEN WATER FREEDIVER",
            "ADVANCED FREEDIVER",
            "MASTER FREEDIVER",
        ]),
        instructor: [],
    },
    MOLCHANOVS: {
        student: tag("freediving", ["WAVE 1", "WAVE 2", "WAVE 3"]),
        instructor: tag("freediving", [
            "LAB 1",
            "LAB 2",
            "LAB 3",
            "LAB 4(Master Instructor/Trainer)",
        ]),
    },
    SDI: {
        student: tag("scuba", [
            "Open Water Scuba Diver",
            "Advanced Diver",
            "Divemaster",
        ]),
        instructor: [],
    },
    WASE: {
        student: tag("scuba", [
            "OpenWater Diver",
            "Advancerd OpenWater Diver",
            "Rescue Diver",
            "Divemaster",
        ]),
        instructor: [],
    },
};

/** 단체/강사여부 기준으로 레벨 배열({name, sport})을 반환한다. */
const getLevelEntries = (company, isInstructor) => {
    const levels = LICENSE_LEVELS[company?.toUpperCase()];
    if (!levels) return [];
    return (isInstructor ? levels.instructor : levels.student) ?? [];
};

/**
 * 선택한 스포츠/강사여부에 해당하는 레벨을 하나라도 가진 단체 목록을 반환한다.
 * @param {string} sport 스포츠 종목 값 (scuba/freediving/mermaid)
 * @param {boolean} isInstructor true면 강사, false면 수강생 기준
 * @returns {string[]} 단체 목록 (노출 순서 유지)
 */
export const getLicenseCompanies = (sport, isInstructor) => {
    return LICENSE_COMPANIES.filter((company) => {
        const entries = getLevelEntries(company, isInstructor);
        return sport
            ? entries.some((e) => e.sport === sport)
            : entries.length > 0;
    });
};

/**
 * 단체명/강사여부/스포츠로 레벨명 목록을 반환한다.
 * @param {string} company 단체명 (대소문자 무관)
 * @param {boolean} isInstructor true면 강사, false면 수강생 레벨
 * @param {string} [sport] 스포츠 종목 값. 지정 시 해당 종목만 필터링
 * @returns {string[]} 레벨명 목록 (없으면 빈 배열)
 */
export const getLicenseLevels = (company, isInstructor, sport) => {
    const entries = getLevelEntries(company, isInstructor);
    return entries
        .filter((e) => !sport || e.sport === sport)
        .map((e) => e.name);
};
